import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import i18n, { SupportedLocale } from "@/i18n";
import { multipleprintsLinksColumnsVisibility } from "@/config/dashboard/multipleprints";
import { computeFieldMultipleprintRelatedAuthors } from "@/lib/computeFields";
import { Multipleprint } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface MultipleprintInMultiplesampleLinksProps {
  id: string;
  type: LinkableType;
}

const RELATION_TABLE = "multipleprintInMultipleSample" as const;

const MultipleprintInMultiplesampleLinks = ({
  id,
  type,
}: MultipleprintInMultiplesampleLinksProps) => {
  const { t } = useTranslation();

  const [
    getPaginationRelatedMultipleprints,
    setPaginationRelatedMultipleprints,
  ] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    RELATION_TABLE,
    getPaginationRelatedMultipleprints.pageSize,
    getPaginationRelatedMultipleprints.page *
      getPaginationRelatedMultipleprints.pageSize,
  );

  const { data: countRelatedMultipleprints } = useCountRelatedFields(id, type, [
    RELATION_TABLE,
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    RELATION_TABLE,
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    RELATION_TABLE,
  );

  const [searchContent, setSearchContent] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedMultiple } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (multipleprintId: string) => {
      deleteRelation(multipleprintId);
    },
    [deleteRelation],
  );

  const STORAGE_KEY = "multipleprint-multiplesample-links-table";
  const defaultVisibility = { ...multipleprintsLinksColumnsVisibility };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );

  const columns: ColumnDef<Multipleprint, unknown>[] = useMemo(() => {
    return [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-nowrap">
            <p className="text-[14px] font-[500]">
              {row.original.estampesMultiplesDescriptionNumeroEnregistrement}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multipleprint"
          />
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        size: 200,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-wrap">
            <p className="text-[14px] font-[500]">
              {row.original.estampesMultiplesDescriptionTitre}
            </p>
          </div>
        ),
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        size: 200,
        cell: ({ row }) => {
          const authors = computeFieldMultipleprintRelatedAuthors(
            row.original,
            { language: i18n.language as SupportedLocale },
          );
          return <TextCell value={authors} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionDateDeCreation",
        header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
        size: 200,
        cell: ({ row }) => (
          <TextCell
            value={row.original.estampesMultiplesDescriptionDateDeCreation}
          />
        ),
      },
            createActionsColumnDef<Multipleprint>(({ row }) => (
        <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={row.original.id}
          previewType="multipleprint"
          previewElements={[
            {
              label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
              value: row.original.estampesMultiplesDescriptionNumeroEnregistrement,
              type: "text",
            },
            {
              label: t("estampesMultiplesDescriptionResume"),
              value: row.original.estampesMultiplesDescriptionResume,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: row.original.attachments,
              type: "image",
            },
          ]}
          onDelete={() => handleDeleteRelation(row.original.id)}
          onAdd={() => handleCreateRelation(row.original.id)}
        />
      )),
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedMultipleprints")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode ? (notRelatedMultiple?.data ?? []) : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: notRelatedMultiple?.totalSize ?? 0,
              }
            : {
                pagination: getPaginationRelatedMultipleprints,
                setPage: setPaginationRelatedMultipleprints,
                totalSize: countRelatedMultipleprints?.[RELATION_TABLE] ?? 0,
              }
        }
        storageKey={STORAGE_KEY}
        options={{
          enableColumnResizing: true,
          onColumnVisibilityChange: setColumnVisibility,
          state: { columnVisibility },
        }}
      />
    </div>
  );
};

export default MultipleprintInMultiplesampleLinks;
